import { deleteDoc, doc, setDoc, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from './firebase';

export const EVALUATION_CODE_LOOKUP_COLLECTION = 'evaluationCodeLookup';
export const GAME_UPLOAD_CODE_COLLECTION = 'gameUploadCodes';
/** 읽기 불가. 해시만 저장하고 원문 토큰은 localStorage에만 둔다. */
export const GAME_EDIT_SECRET_COLLECTION = 'gameEditSecrets';
/** 컴시간 callable과 같이 asia-northeast3. 배포 전에는 게스트 수정이 실패한다. */
export const GUEST_GAME_EDIT_FUNCTION_REGION = 'asia-northeast3';

const GUEST_UPLOADER_KEY = 'maru_arcade_guest_uploader_id';
const GUEST_GAME_IDS_KEY = 'maru_arcade_guest_game_ids';
const GUEST_EDIT_TOKENS_KEY = 'maru_arcade_guest_edit_tokens';

export interface GuestGameEditableFields {
  title: string;
  description: string;
  introduction: string;
  gameUrl: string;
  simulatorCode: string;
  processUrl: string;
  companyName: string;
  creatorName: string;
  grade: number | null;
  class: number | null;
  commentsOnly: boolean;
  allowGuestComments: boolean;
}

export function normalizeTeacherCode(code: string): string {
  return code.trim().toUpperCase();
}

/** 회원가입 없이 게임을 올린 브라우저의 작성자 ID */
export function getGuestUploaderId(): string {
  const saved = localStorage.getItem(GUEST_UPLOADER_KEY);
  if (saved && /^guest_[a-zA-Z0-9_-]{8,80}$/.test(saved)) {
    return saved;
  }
  const random = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID().replace(/-/g, '')
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  const id = `guest_${random}`;
  localStorage.setItem(GUEST_UPLOADER_KEY, id);
  return id;
}

export function readGuestGameIds(): string[] {
  try {
    const raw = localStorage.getItem(GUEST_GAME_IDS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string' && id.length > 0);
  } catch {
    return [];
  }
}

export function rememberGuestGameId(gameId: string): void {
  const ids = [gameId, ...readGuestGameIds().filter((id) => id !== gameId)].slice(0, 50);
  localStorage.setItem(GUEST_GAME_IDS_KEY, JSON.stringify(ids));
}

function toTimestamp(value: Date | Timestamp | { toDate: () => Date }): Timestamp {
  if (value instanceof Timestamp) return value;
  if (value instanceof Date) return Timestamp.fromDate(value);
  if (value && typeof value.toDate === 'function') return Timestamp.fromDate(value.toDate());
  return Timestamp.fromDate(new Date());
}

/** 비회원이 코드만으로 조회·규칙 검증할 수 있게 코드 문자열을 문서 ID로 저장 */
export async function upsertEvaluationCodeLookup(data: {
  code: string;
  schoolId: string;
  schoolName: string;
  grade: number;
  startDate: Date | Timestamp | { toDate: () => Date };
  endDate: Date | Timestamp | { toDate: () => Date };
  reviewerClasses: { [key: string]: string };
  createdBy?: string;
}): Promise<void> {
  const code = normalizeTeacherCode(data.code);
  if (!/^[A-Z0-9]{4,16}$/.test(code)) return;
  await setDoc(doc(db, EVALUATION_CODE_LOOKUP_COLLECTION, code), {
    code,
    schoolId: data.schoolId,
    schoolName: data.schoolName,
    grade: data.grade,
    startDate: toTimestamp(data.startDate),
    endDate: toTimestamp(data.endDate),
    reviewerClasses: data.reviewerClasses || {},
    createdBy: data.createdBy || '',
  });
}

export async function deleteEvaluationCodeLookup(code: string): Promise<void> {
  const normalized = normalizeTeacherCode(code);
  if (!normalized) return;
  await deleteDoc(doc(db, EVALUATION_CODE_LOOKUP_COLLECTION, normalized));
}

const ID_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

export function createGuestGameId(): string {
  const bytes = new Uint8Array(20);
  crypto.getRandomValues(bytes);
  let id = '';
  for (let index = 0; index < bytes.length; index += 1) {
    id += ID_ALPHABET[bytes[index] % ID_ALPHABET.length];
  }
  return id;
}

export function createGuestEditToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Cloud Function과 같은 입력: sha256(`${gameId}:${token}`) */
export async function hashGuestEditToken(gameId: string, token: string): Promise<string> {
  const encoded = new TextEncoder().encode(`${gameId}:${token}`);
  const digest = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function readGuestEditTokenMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(GUEST_EDIT_TOKENS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Record<string, string>;
  } catch {
    return {};
  }
}

export function rememberGuestEditToken(gameId: string, token: string): void {
  const map = readGuestEditTokenMap();
  map[gameId] = token;
  const pruned: Record<string, string> = {};
  readGuestGameIds().forEach((id) => {
    if (typeof map[id] === 'string') pruned[id] = map[id];
  });
  localStorage.setItem(GUEST_EDIT_TOKENS_KEY, JSON.stringify(pruned));
}

export function readGuestEditToken(gameId: string): string | null {
  const token = readGuestEditTokenMap()[gameId];
  return typeof token === 'string' && /^[a-f0-9]{64}$/.test(token) ? token : null;
}

function boundedText(value: string, max: number, label: string): string {
  const trimmed = value.trim();
  if (trimmed.length > max) {
    throw new Error(`${label}은 ${max}자 이하로 입력해 주세요.`);
  }
  return trimmed;
}

function optionalGradeOrClass(value: number | null, max: number, label: string): number | null {
  if (value == null || Number.isNaN(value)) return null;
  if (!Number.isInteger(value) || value < 1 || value > max) {
    throw new Error(`${label}은 1~${max} 사이 숫자로 입력해 주세요.`);
  }
  return value;
}

/** 등록 폼이 바꾸는 내용 필드만 남긴다. 소유권·평가 여부는 포함하지 않는다. */
export function toGuestGameEditableFields(input: GuestGameEditableFields): GuestGameEditableFields {
  const title = boundedText(input.title || '', 120, '게임 제목');
  const gameUrl = boundedText(input.gameUrl || '', 500, '게임 URL');
  const simulatorCode = boundedText(input.simulatorCode || '', 20000, '시뮬레이터 코드');
  const creatorName = boundedText(input.creatorName || '', 40, '제작자 이름');
  if (!title) throw new Error('게임 제목을 입력해 주세요.');
  if (!gameUrl) throw new Error('게임 URL을 입력해 주세요.');
  if (!simulatorCode) throw new Error('시뮬레이터 코드를 입력해 주세요.');
  if (!creatorName) throw new Error('제작자 이름을 입력해 주세요.');
  return {
    title,
    description: boundedText(input.description || '', 5000, '게임 설명'),
    introduction: boundedText(input.introduction || '', 5000, '게임 소개'),
    gameUrl,
    simulatorCode,
    processUrl: boundedText(input.processUrl || '', 500, '제작 과정 URL'),
    companyName: boundedText(input.companyName || '', 80, '제작 회사명'),
    creatorName,
    grade: optionalGradeOrClass(input.grade, 6, '학년'),
    class: optionalGradeOrClass(input.class, 30, '반'),
    commentsOnly: Boolean(input.commentsOnly),
    allowGuestComments: Boolean(input.allowGuestComments),
  };
}

export function toCodeIssuerGameUpdate(
  input: GuestGameEditableFields,
  iconUrl?: string
): Record<string, string | number | boolean | null> {
  const fields = toGuestGameEditableFields(input);
  const payload: Record<string, string | number | boolean | null> = { ...fields };
  if (!iconUrl) return payload;
  const trimmedIcon = iconUrl.trim();
  if (!trimmedIcon || trimmedIcon.length > 2048) {
    throw new Error('아이콘 주소가 올바르지 않습니다.');
  }
  payload.iconUrl = trimmedIcon;
  return payload;
}

function guestEditErrorMessage(error: unknown): string {
  const code = error && typeof error === 'object' && 'code' in error
    ? String((error as { code?: string }).code || '')
    : '';
  const message = error && typeof error === 'object' && 'message' in error
    ? String((error as { message?: string }).message || '').trim()
    : '';
  if (code.includes('not-found') || code.includes('unavailable') || code.includes('internal')) {
    return '게임 수정 서버가 아직 배포되지 않았습니다. 함수를 배포한 뒤 다시 시도해 주세요.';
  }
  if (code.includes('permission-denied')) {
    return '이 게임을 수정할 수 없습니다. 등록했던 브라우저에서만 수정할 수 있습니다.';
  }
  if (code.includes('invalid-argument') && message) return message;
  if (message && !/^(internal|unknown|error)$/i.test(message)) return message;
  return '게임을 수정하지 못했습니다.';
}

export async function submitGuestGameEdit(
  gameId: string,
  editToken: string,
  updates: GuestGameEditableFields
): Promise<void> {
  const fields = toGuestGameEditableFields(updates);
  const functions = getFunctions(app, GUEST_GAME_EDIT_FUNCTION_REGION);
  const callable = httpsCallable<
    { gameId: string; editToken: string; updates: GuestGameEditableFields },
    { success: boolean }
  >(functions, 'updateGuestUploadedGame');
  try {
    await callable({ gameId, editToken, updates: fields });
  } catch (error) {
    throw new Error(guestEditErrorMessage(error));
  }
}
